import qs from 'qs';
import { type WfsVersion } from '../MapLayer.ts';
import { parseLayerAndOptions } from '../rendering/layerUrl.ts';

/** Singolo feature type dichiarato dal GetCapabilities. */
export interface IWfsFeatureTypeInfo {
  /** Nome qualificato (es. 'osm:water_areas') */
  name: string;
  title?: string;
  /** CRS nativo dichiarato (DefaultCRS/DefaultSRS), normalizzato */
  defaultCrs?: string;
  /** outputFormat dichiarati a livello di feature type (spesso vuoto) */
  outputFormats: string[];
}

export interface IWfsCapabilities {
  version: WfsVersion;
  featureTypes: IWfsFeatureTypeInfo[];
  /** outputFormat dichiarati per l'operazione GetFeature */
  getFeatureOutputFormats: string[];
}

const SUPPORTED_WFS_VERSIONS: WfsVersion[] = ['1.1.0', '2.0.0'];

/**
 * Normalizza un identificatore CRS nei vari formati OGC (codice EPSG,
 * URN, URI) alla forma `EPSG:nnnn`. Restituisce undefined per i formati
 * non riconosciuti.
 */
export function normalizeCrsIdentifier(
  value: string | undefined,
): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();

  if (/^EPSG:\d+$/i.test(trimmed)) return trimmed.toUpperCase();

  // es. urn:ogc:def:crs:EPSG::25832, urn:x-ogc:def:crs:EPSG:6.9:25832
  const urnMatch = trimmed.match(/^urn:[^:]*:def:crs:EPSG:[^:]*:(\d+)$/i);
  if (urnMatch) return `EPSG:${urnMatch[1]}`;

  // es. http://www.opengis.net/def/crs/EPSG/0/25832
  const uriMatch = trimmed.match(/def\/crs\/EPSG\/[^/]+\/(\d+)$/i);
  if (uriMatch) return `EPSG:${uriMatch[1]}`;

  // CRS84 è WGS84 con assi lon/lat: equivale a EPSG:4326 per i client
  if (/CRS84$/i.test(trimmed)) return 'EPSG:4326';

  return undefined;
}

/** URL della richiesta GetCapabilities, preservando i parametri esistenti. */
export function buildWfsCapabilitiesUrl(rawUrl: string): string {
  const { url, options: existingParams } = parseLayerAndOptions(rawUrl);
  const params = {
    ...existingParams,
    service: 'WFS',
    request: 'GetCapabilities',
    // Negoziazione versione: il server risponde con la più alta supportata
    acceptVersions: '2.0.0,1.1.0',
  };
  return `${url}?${qs.stringify(params)}`;
}

function childrenByLocalName(parent: Element, localName: string): Element[] {
  return Array.from(parent.children).filter(
    element => element.localName === localName,
  );
}

function childTextByLocalName(
  parent: Element,
  localName: string,
): string | undefined {
  const text = childrenByLocalName(parent, localName)[0]?.textContent?.trim();
  return text || undefined;
}

function descendantsByLocalName(root: Element, localName: string): Element[] {
  return Array.from(root.getElementsByTagNameNS('*', localName));
}

function parseFeatureType(element: Element): IWfsFeatureTypeInfo | null {
  const name = childTextByLocalName(element, 'Name');
  if (!name) return null;

  // DefaultCRS in WFS 2.0.0, DefaultSRS in WFS 1.1.0
  const rawCrs =
    childTextByLocalName(element, 'DefaultCRS') ??
    childTextByLocalName(element, 'DefaultSRS');

  const outputFormats = childrenByLocalName(element, 'OutputFormats').flatMap(
    formats =>
      childrenByLocalName(formats, 'Format')
        .map(format => format.textContent?.trim() ?? '')
        .filter(format => format.length > 0),
  );

  return {
    name,
    title: childTextByLocalName(element, 'Title'),
    defaultCrs: normalizeCrsIdentifier(rawCrs),
    outputFormats,
  };
}

function parseGetFeatureOutputFormats(root: Element): string[] {
  const operations = descendantsByLocalName(root, 'Operation').filter(
    operation => operation.getAttribute('name') === 'GetFeature',
  );

  return operations.flatMap(operation =>
    Array.from(operation.getElementsByTagNameNS('*', 'Parameter'))
      .filter(parameter => parameter.getAttribute('name') === 'outputFormat')
      .flatMap(parameter =>
        // In WFS 2.0.0 i valori sono dentro <ows:AllowedValues>, in 1.1.0
        // sono figli diretti: la ricerca per discendenza copre entrambi
        Array.from(parameter.getElementsByTagNameNS('*', 'Value'))
          .map(value => value.textContent?.trim() ?? '')
          .filter(value => value.length > 0),
      ),
  );
}

/**
 * Interpreta la risposta XML di un GetCapabilities WFS (1.1.0 o 2.0.0).
 * La ricerca degli elementi è per `localName`, indipendente dai prefissi
 * di namespace: la struttura varia tra GeoServer, ArcGIS Server, ecc.
 *
 * @throws Error se il documento non è un WFS_Capabilities o la versione
 *   non è supportata.
 */
export function parseWfsCapabilities(xml: string): IWfsCapabilities {
  const document = new DOMParser().parseFromString(xml, 'text/xml');

  const parserError = document.getElementsByTagName('parsererror')[0];
  const root = document.documentElement;
  if (parserError || !root || root.localName !== 'WFS_Capabilities') {
    throw new Error('La risposta non è un GetCapabilities WFS valido');
  }

  const version = root.getAttribute('version') ?? '';
  if (!SUPPORTED_WFS_VERSIONS.includes(version as WfsVersion)) {
    throw new Error(`Versione WFS non supportata: ${version || 'sconosciuta'}`);
  }

  const featureTypes = descendantsByLocalName(root, 'FeatureType')
    .map(parseFeatureType)
    .filter(
      (featureType): featureType is IWfsFeatureTypeInfo => featureType !== null,
    );

  return {
    version: version as WfsVersion,
    featureTypes,
    getFeatureOutputFormats: parseGetFeatureOutputFormats(root),
  };
}

/**
 * Scarica e interpreta il GetCapabilities di un servizio WFS.
 *
 * @throws Error per errori di rete, HTTP o parsing (gestiti dal chiamante,
 *   tipicamente react-query).
 */
export async function fetchWfsCapabilities(
  rawUrl: string,
): Promise<IWfsCapabilities> {
  const response = await fetch(buildWfsCapabilitiesUrl(rawUrl));
  if (!response.ok) {
    throw new Error(`GetCapabilities fallita con HTTP ${response.status}`);
  }
  return parseWfsCapabilities(await response.text());
}

/** True se tra i formati dichiarati c'è una variante GeoJSON. */
export function findGeoJsonOutputFormat(formats: string[]): string | undefined {
  return formats.find(format => format.toLowerCase().includes('json'));
}
